import React, { useEffect, useState } from 'react';
import { db, functions } from '../firebase';
import {
  collection,
  getDocs,
  query,
  orderBy,
  addDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useNavigate } from 'react-router-dom';

export default function StripeReceipts() {
  const navigate = useNavigate();
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const q = query(collection(db, 'bookings'), orderBy('createdAt', 'desc'));
        const snap = await getDocs(q);
        const data = snap.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(b => b.receiptUrl);
        setReceipts(data);
      } catch (e) {
        console.error('❌ Failed to fetch receipts:', e);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const formatDate = ts =>
    ts?.seconds ? new Date(ts.seconds * 1000).toLocaleDateString() : '—';

  const matchesFilters = b => {
    const date = b.createdAt?.seconds ? new Date(b.createdAt.seconds * 1000) : null;
    const from = startDate ? new Date(startDate) : null;
    const to = endDate ? new Date(endDate) : null;
    const matchesStatus = statusFilter === 'all' || b.status === statusFilter;
    const matchesSearch =
      !searchTerm ||
      b.bookedByName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      b.bookedByEmail?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDate = (!from || date >= from) && (!to || date <= to);
    return matchesStatus && matchesSearch && matchesDate;
  };

  const filtered = receipts.filter(matchesFilters);

  const exportCSV = () => {
    const headers = ['Date', 'User', 'Listing', 'Total Paid', 'Receipt', 'Status'];
    const rows = filtered.map(r => [
      formatDate(r.createdAt),
      r.bookedByName,
      r.listingTitle,
      `$${r.totalCost?.toFixed(2)}`,
      r.receiptUrl,
      r.status,
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'stripe_receipts.csv';
    a.click();
  };

  const refundBooking = async booking => {
    try {
      const confirm = window.confirm('Are you sure you want to issue a refund?');
      if (!confirm) return;

      const refundFn = httpsCallable(functions, 'refundBooking');
      await refundFn({ bookingId: booking.id });

      const currentUser = JSON.parse(localStorage.getItem('firebase:authUser'));
      await addDoc(collection(db, 'logs'), {
        action: 'Refund Issued',
        bookingId: booking.id,
        adminEmail: currentUser?.email || 'unknown',
        timestamp: serverTimestamp(),
      });

      alert('Refund issued.');
      window.location.reload();
    } catch (err) {
      console.error('❌ Refund failed:', err);
      alert('Refund failed.');
    }
  };

  const statusBadge = status => {
    if (status === 'paid') return '✅ Paid';
    if (status === 'refunded') return '🔁 Refunded';
    if (status === 'failed') return '❌ Failed';
    return status;
  };

  return (
    <div style={styles.container}>
      <button onClick={() => navigate('/dashboard')} style={styles.backButton}>
        ← Back to Dashboard
      </button>
      <h2 style={styles.title}>💳 Stripe Receipts</h2>

      <div style={styles.filters}>
        <input
          type="text"
          placeholder="Search by name/email"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          style={styles.input}
        />
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          style={styles.select}
        >
          <option value="all">All</option>
          <option value="paid">Paid</option>
          <option value="refunded">Refunded</option>
          <option value="failed">Failed</option>
        </select>
        <input
          type="date"
          value={startDate}
          onChange={e => setStartDate(e.target.value)}
          style={styles.input}
        />
        <input
          type="date"
          value={endDate}
          onChange={e => setEndDate(e.target.value)}
          style={styles.input}
        />
        <button onClick={exportCSV} style={styles.export}>
          Export CSV
        </button>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Date</th>
              <th style={styles.th}>User</th>
              <th style={styles.th}>Listing</th>
              <th style={styles.th}>Total Paid</th>
              <th style={styles.th}>Receipt</th>
              <th style={styles.th}>Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r, i) => (
              <tr
                key={r.id}
                onClick={() => setSelected(r)}
                style={{ ...styles[(i % 2 === 0 ? 'rowEven' : 'rowOdd')], cursor: 'pointer' }}
              >
                <td style={styles.td}>{formatDate(r.createdAt)}</td>
                <td style={styles.td}>{r.bookedByName}</td>
                <td style={styles.td}>{r.listingTitle}</td>
                <td style={styles.td}>${r.totalCost?.toFixed(2)}</td>
                <td style={styles.td}>
                  <a href={r.receiptUrl} target="_blank" rel="noreferrer">
                    View
                  </a>
                </td>
                <td style={styles.td}>{statusBadge(r.status)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {selected && (
        <div style={styles.modalOverlay} onClick={() => setSelected(null)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <h3>Receipt Details</h3>
            <p><strong>Booking ID:</strong> {selected.id}</p>
            <p><strong>Payment Intent ID:</strong> {selected.paymentIntentId || '—'}</p>
            <p><strong>Subtotal:</strong> ${selected.subtotal?.toFixed(2)}</p>
            <p><strong>Platform Fee:</strong> ${selected.platformFee?.toFixed(2)}</p>
            <p><strong>Insurance:</strong> {selected.insuranceType || 'None'}</p>
            <p><strong>Status:</strong> {statusBadge(selected.status)}</p>
            <p>
              <a href={selected.receiptUrl} target="_blank" rel="noreferrer">
                View Stripe Receipt
              </a>
            </p>
            {selected.status === 'paid' && (
              <button onClick={() => refundBooking(selected)} style={styles.refund}>
                🧾 Issue Refund
              </button>
            )}
            <button onClick={() => setSelected(null)} style={styles.close}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    padding: '2rem',
    fontFamily: 'system-ui',
    backgroundColor: '#f9f9f9',
    minHeight: '100vh',
  },
  backButton: {
    backgroundColor: '#eeeeee',
    border: '1px solid #ccc',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: 500,
    marginBottom: '1rem',
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 600,
    color: '#065f46', // dark green
    marginBottom: '1rem',
  },
  filters: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '1rem',
    flexWrap: 'wrap',
  },
  input: {
    padding: '0.4rem',
    fontSize: '0.9rem',
  },
  select: {
    padding: '0.4rem',
    fontSize: '0.9rem',
  },
  export: {
    backgroundColor: '#065f46', // dark green
    color: 'white',
    border: 'none',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: '#fff',
    borderRadius: '6px',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  th: {
    backgroundColor: '#e0f2f1', 
    color: 'black',
    padding: '0.75rem',
    textAlign: 'left',
    fontSize: '0.875rem',
  },
  td: {
    padding: '0.75rem',
    borderBottom: '1px solid #e5e7eb',
    fontSize: '0.875rem',
  },
  rowEven: {
    backgroundColor: '#f9f9f9',
  },
  rowOdd: {
    backgroundColor: '#ffffff',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modal: {
    backgroundColor: '#fff',
    padding: '2rem',
    borderRadius: '8px',
    maxWidth: '500px',
    width: '90%',
  },
  close: {
    marginTop: '1rem',
    backgroundColor: '#6b7280',
    color: 'white',
    padding: '0.5rem 1rem',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  refund: {
    marginTop: '1rem',
    backgroundColor: '#b91c1c',
    color: 'white',
    padding: '0.5rem 1rem',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
  },
};
